import React from 'react';
import { Card, Row, Col, Typography, Statistic, Progress } from 'antd';
import { TrophyOutlined, RiseOutlined, FallOutlined, BarChartOutlined, SafetyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '../../theme/ThemeProvider';

const { Title, Text } = Typography;

interface HoverCardProps {
  data: any;
  rankGroup: string;
  period: string;
  visible: boolean;
  position: { x: number; y: number };
}

const HoverCard: React.FC<HoverCardProps> = ({ data, rankGroup, period, visible, position }) => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();

  if (!visible || !data) return null;

  const getWinRateColor = (rate: number) => {
    if (rate >= 60) return '#52c41a';
    if (rate >= 40) return '#faad14';
    return '#ff4d4f';
  };

  const getReturnColor = (return_: number) => {
    if (return_ > 0) return '#52c41a';
    return '#ff4d4f';
  };

  const getVolatilityColor = (volatility: number) => {
    if (volatility <= 2.0) return '#52c41a';
    if (volatility <= 5.0) return '#faad14';
    if (volatility <= 10.0) return '#ff7a45';
    return '#ff4d4f';
  };

  const getConfidenceScore = (sampleSize: number, winRate: number) => {
    if (sampleSize >= 50 && winRate >= 60) return { score: '高', color: '#52c41a' };
    if (sampleSize >= 20 && winRate >= 50) return { score: '中', color: '#faad14' };
    return { score: '低', color: '#ff4d4f' };
  };

  const confidence = getConfidenceScore(data.sample_count, data.win_rate);

  return (
    <div
      style={{
        position: 'fixed',
        left: position.x + 10,
        top: position.y - 10,
        zIndex: 1000,
        pointerEvents: 'none'
      }}
    >
      <Card
        size="small"
        style={{
          width: 280,
          borderRadius: 8,
          border: `1px solid ${theme.surfaceBorder}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          background: theme.surfaceBackground,
          color: theme.textPrimary
        }}
      >
        {/* 标题 */}
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <Title level={5} style={{ margin: 0, color: theme.accentPrimary }}>
            {rankGroup} ({period} K-lines)
          </Title>
        </div>

        {/* 主要指标 */}
        <Row gutter={[8, 8]}>
          <Col span={12}>
            <Statistic
              title={t('analysis.winRate')}
              value={data.win_rate?.toFixed(1)}
              suffix="%"
              valueStyle={{ 
                color: getWinRateColor(data.win_rate),
                fontSize: '16px',
                fontWeight: 'bold'
              }}
              prefix={<TrophyOutlined />}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title={t('analysis.averageReturn')}
              value={data.mean_return?.toFixed(2)}
              suffix="%"
              valueStyle={{ 
                color: getReturnColor(data.mean_return),
                fontSize: '16px',
                fontWeight: 'bold'
              }}
              prefix={data.mean_return >= 0 ? <RiseOutlined /> : <FallOutlined />}
            />
          </Col>
        </Row>

        {/* 进度条显示胜率 */}
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: '12px', color: theme.textSecondary }}>
            {t('analysis.winRate')} {data.win_rate?.toFixed(1)}%
          </Text>
          <Progress 
            percent={data.win_rate} 
            size="small" 
            strokeColor={getWinRateColor(data.win_rate)}
            showInfo={false}
            style={{ marginTop: 4 }}
          />
        </div>

        {/* 风险指标 */}
        <div style={{ 
          marginTop: 12, 
          padding: '8px', 
          background: theme.toolbarBackground, 
          borderRadius: 4,
          border: `1px solid ${theme.surfaceBorder}`
        }}>
          <Row gutter={[8, 4]}>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: '11px', color: theme.textSecondary }}>
                {t('analysis.maxDrawdown')}:
              </Text>
              <div style={{ 
                color: '#ff4d4f', 
                fontSize: '12px', 
                fontWeight: 'bold' 
              }}>
                {Math.abs(data.max_drawdown)?.toFixed(2)}%
              </div>
            </Col>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: '11px', color: theme.textSecondary }}>
                {t('analysis.volatility')}:
              </Text>
              <div style={{ 
                color: getVolatilityColor(data.volatility), 
                fontSize: '12px', 
                fontWeight: 'bold' 
              }}>
                {data.volatility?.toFixed(2)}
              </div>
            </Col>
          </Row>
        </div>

        {/* 样本数和信心分数 */}
        <Row gutter={8} style={{ marginTop: 8 }}>
          <Col span={12}>
            <div style={{ textAlign: 'center' }}>
              <BarChartOutlined style={{ color: theme.accentPrimary, fontSize: '14px' }} />
              <div style={{ fontSize: '12px', color: theme.textSecondary }}>
                {t('analysis.sampleSize')}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: theme.accentPrimary }}>
                {data.sample_count}
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ textAlign: 'center' }}>
              <SafetyOutlined style={{ color: confidence.color, fontSize: '14px' }} />
              <div style={{ fontSize: '12px', color: theme.textSecondary }}>
                {t('analysis.confidenceScore')}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: confidence.color }}>
                {confidence.score}
              </div>
            </div>
          </Col>
        </Row>

        {/* 趋势强度 */}
        {data.trend_strength !== undefined && (
          <div style={{ marginTop: 8, textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: '11px', color: theme.textSecondary }}>
              {t('analysis.trendStrength')}:
            </Text>
            <div style={{ 
              color: data.trend_strength > 0 ? '#52c41a' : '#ff4d4f', 
              fontSize: '12px', 
              fontWeight: 'bold' 
            }}>
              {data.trend_strength?.toFixed(1)}%
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default HoverCard; 